'use client';

import { useTranslations } from "next-intl";
import Empty from './empty';
import moreImg from '@/features/notifications/images/arrow-right-blue.png';
import Image from 'next/image';
import {
  NotificationItem,
  setNotificationRead,
  NotificationReadStatus,
  NotificationListRes,
  getNotificationByPage
} from "@/features/notifications/services";
import { NotificationType } from "../constant";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useEffect } from "react";
import useIsLogin from "@/features/notifications/hooks/useIsLogin";
import MsgCard from './msg-card';
import { Spin } from '@/components/ui-feat/spin';
import { formatSecondToDate } from '@/utils/time';

interface PlatformListProps {
  decUnreadNumAction: (type: NotificationType) => void;
  trackRead: (msgItem: NotificationItem) => void;
  hasReadAll: boolean;
  // 点击底部按钮跳转事件
  onJumpClick: (msgItem: NotificationItem, btnName: string) => void;
}
export default function PlatformItem({ decUnreadNumAction, trackRead, hasReadAll, onJumpClick }: PlatformListProps) {
  // const t = useTranslations('notifications'); // 多语言模块
  const tCommon = useTranslations('common');
  const { isLogin } = useIsLogin();

  function fetchData(page: number, pageSize: number): Promise<NotificationListRes> {
    // 只在登录时才请求数据
    if (!isLogin) {
      return Promise.resolve({ list: [], total: 0 });
    }
    return getNotificationByPage({
      msg_type: NotificationType.Platform,
      page: page,
      size: pageSize
    });
  }
  const { items, setItems, containerRef, loading, hasMore } = useInfiniteScroll(fetchData, 10);

  async function setReadMsg(item: NotificationItem) {
    // 只在登录时才允许设置已读
    if (!isLogin) return null;
    const res = await setNotificationRead({ id: item.id });
    return res;
  }
  async function readMsg(item: NotificationItem) {
    // 埋点上报
    trackRead(item);
    const res = await setReadMsg(item);
    if (res?.status === 1) {
      updateMsgData({
        ...item,
        is_read: NotificationReadStatus.READED,
      });
    }
  }
  async function toggleShowAllMsg(item: NotificationItem, isShowAll: boolean) {
    const newMsg = {
      ...item,
      isShowAll: isShowAll
    };
    if (item.is_read === NotificationReadStatus.UNREAD && isShowAll) {
      // 埋点上报
      // trackRead(item, t('showAll'));
      trackRead(item);
      // 点击展开后，将消息设置为已读
      const res = await setReadMsg(item);
      if (res?.status === 1) {
        newMsg.is_read = NotificationReadStatus.READED;
      }
    }
    updateMsgData(newMsg);
  }
  // 平台类型消息，直接跳转
  function handleJump(detail: NotificationItem, path: string, btnName: string) {
    if (detail.is_read === NotificationReadStatus.UNREAD) {
      readMsg(detail);
    }
    onJumpClick(detail, btnName);
    window.open(path, '_blank');
  }

  function updateMsgData(item: NotificationItem) {
    setItems(
      items.map((msgItem) => {
        if (msgItem.id === item.id) {
          // 更新父组件未读数量
          if (msgItem.is_read === NotificationReadStatus.UNREAD && item.is_read === NotificationReadStatus.READED) {
            decUnreadNumAction(NotificationType.Platform);
          }
          return item;
        }
        return msgItem;
      })
    );
  }
  // 父组件已读全部 则将所有消息设置为已读状态
  useEffect(() => {
    if (hasReadAll) {
      setItems(preList => preList.map((item) => {
        // 只有未读消息才设置为已读
        if (item.is_read === NotificationReadStatus.UNREAD) {
          // 埋点上报
          // trackRead(item, t('markAllAsRead'));
        }
        return ({ ...item, is_read: NotificationReadStatus.READED });
      }));
    }
  }, [hasReadAll, setItems]);

  const btnGroupSlot = (detail: NotificationItem) => {
    if (!Array.isArray(detail.button_links) || detail.button_links.length === 0) {
      return null;
    }

    const validLinks = detail.button_links
      .filter(link => link.button_link && link.button_text)
      .slice(0, 2);

    return (
      <div className="flex">
        {validLinks.map((item, index) => (
          <div
            key={`${detail.id}-${item.button_link}-${index}`}
            className="flex items-center text-accent-blue text-[12px] font-medium mr-[15px] cursor-pointer pressable active:scale-90"
            onClick={() => handleJump(detail, item.button_link, item.button_text)}
          >
            <span className="max-w-[80px] truncate">{item.button_text}</span>
            <Image src={moreImg} alt="" className="w-[6px] h-[10px] ml-[5px]" />
          </div>
        ))}
      </div>
    );
  };


  return (
    <div className="overflow-y-auto h-[calc(80vh-110px)] mt-[15px] no-scrollbar box-border px-[12px]" ref={containerRef}>
      {items.length === 0 && loading && <Spin className='w-full h-full' />}
      {items.length === 0 && !loading && <Empty showText />}
      {
        items.length > 0 && items.map((detail, index) => {
          return (
            <MsgCard className="mb-[20px]" key={`${detail.id}-${index}`} msgId={detail.id} title={detail.title} content={detail.content} time={formatSecondToDate(detail.create_at, 'DD/MM HH:mm')} coverUrl={detail.img_urls} isRead={detail.is_read === NotificationReadStatus.READED} msgDetail={detail} onReadClick={readMsg} onExpandClick={toggleShowAllMsg} btnGroupSlot={btnGroupSlot(detail)} />
          );
        })
      }
      {loading && items.length > 0 && (
        <div className="flex items-center justify-center py-4">
          <div className="text-tertiary text-sm">{tCommon('loading')}</div>
        </div>
      )}
      {!hasMore && items.length > 0 && (
        <div className="flex items-center justify-center py-4">
          <div className="text-tertiary text-xs">{tCommon('noMoreData')}</div>
        </div>
      )}
    </div>
  );
}
